/**
 * EmailPopup.js — Email Popup (UC-06)
 *
 * Per FSD: "Triggered by 'Email Plan' ... Fields: Email (required if
 * submitting), 'Receive Yoga Tips' checkbox (optional, unchecked by
 * default). Submitting logs an EmailLead record and triggers UC-06."
 *
 * There's no real Email/Notification Service endpoint specified yet, so
 * onSubmit falls back to a local mock (resolves after a short delay) —
 * pass a real onSubmit from DietPlannerWizard once UC-06's backend call
 * exists; this component's retry-without-losing-the-address behavior
 * (UC-06 Exception Flow) doesn't need to change either way.
 */

import React, { useCallback, useMemo, useState } from "react";
import useEmailPopupAnalytics from "./useEmailPopupAnalytics";
import { validateEmailPopup } from "./EmailPopup.validation";
import {
  Overlay,
  Modal,
  ModalTitle,
  ModalSubtitle,
  FieldGroup,
  FieldLabel,
  EmailInput,
  ErrorText,
  CheckboxRow,
  ButtonRow,
  SubmitButton,
  CancelButton,
  ConfirmationText,
} from "./EmailPopup.styles";

// TODO: replace with a real UC-06 call (creates the EmailLead record and
// dispatches the plan PDF via the Email/Notification Service).
function defaultMockSubmit({ email, receiveYogaTips }) {
  return new Promise((resolve) => {
    setTimeout(() => resolve({ email, receiveYogaTips }), 900);
  });
}

/**
 * @param {object}   props
 * @param {string}   props.sessionId
 * @param {function} props.onClose   - Dismiss without submitting.
 * @param {function} [props.onSubmit] - `({ email, receiveYogaTips }) =>
 *   Promise<void>`. Defaults to a local mock.
 */
const EmailPopup = ({ sessionId, onClose, onSubmit }) => {
  const analytics = useEmailPopupAnalytics();
  const [email, setEmail] = useState("");
  const [receiveYogaTips, setReceiveYogaTips] = useState(false);
  const [status, setStatus] = useState("idle"); // "idle" | "submitting" | "sent" | "error"
  const [submitError, setSubmitError] = useState(null);
  // Separate from `status` so a validation-only failed attempt (email
  // format, before any network call) still surfaces the error — `status`
  // only ever describes a network attempt.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const { errors } = useMemo(() => validateEmailPopup({ email }), [email]);

  const handleClose = useCallback(() => {
    analytics.trackDismiss(sessionId);
    onClose?.();
  }, [analytics, sessionId, onClose]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitAttempted(true);
      const { isValid } = validateEmailPopup({ email });
      if (!isValid) return;

      analytics.trackSubmit(sessionId, receiveYogaTips);
      setStatus("submitting");
      setSubmitError(null);

      const submit = onSubmit ?? defaultMockSubmit;
      try {
        await submit({ email: email.trim(), receiveYogaTips });
        analytics.trackSubmitSuccess(sessionId);
        setStatus("sent");
      } catch (err) {
        // UC-06 Exception Flow: "show an inline error and allow retry
        // without losing the entered address" — email/checkbox state is
        // untouched here, only `status` resets to let the form resubmit.
        analytics.trackSubmitFailure(sessionId, err?.message);
        setStatus("error");
        setSubmitError(err?.message || "Couldn't send your plan — please try again.");
      }
    },
    [analytics, sessionId, email, receiveYogaTips, onSubmit]
  );

  return (
    <Overlay role="dialog" aria-modal="true" aria-labelledby="emailPopupTitle" onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        {status === "sent" ? (
          <>
            <ModalTitle id="emailPopupTitle">Sent!</ModalTitle>
            <ConfirmationText>Your plan is on its way to {email}.</ConfirmationText>
            <ButtonRow>
              <SubmitButton type="button" onClick={handleClose}>
                Done
              </SubmitButton>
            </ButtonRow>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <ModalTitle id="emailPopupTitle">Email your plan</ModalTitle>
            <ModalSubtitle>We'll send a PDF copy to your inbox.</ModalSubtitle>

            <FieldGroup>
              <FieldLabel htmlFor="emailPopupEmail">Email address</FieldLabel>
              <EmailInput
                id="emailPopupEmail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                $error={Boolean(errors.email) && submitAttempted}
                autoFocus
              />
              {errors.email && submitAttempted && <ErrorText>{errors.email}</ErrorText>}
            </FieldGroup>

            <CheckboxRow>
              <input
                type="checkbox"
                checked={receiveYogaTips}
                onChange={(e) => setReceiveYogaTips(e.target.checked)}
              />
              Also send me yoga tips
            </CheckboxRow>

            {status === "error" && <ErrorText role="alert">{submitError}</ErrorText>}

            <ButtonRow>
              <CancelButton type="button" onClick={handleClose}>
                Cancel
              </CancelButton>
              <SubmitButton type="submit" disabled={status === "submitting"}>
                {status === "submitting" ? "Sending…" : "Send"}
              </SubmitButton>
            </ButtonRow>
          </form>
        )}
      </Modal>
    </Overlay>
  );
};

EmailPopup.displayName = "EmailPopup";
export default React.memo(EmailPopup);
